import React from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';

import { BetStatusType, IBet, IPriceInput } from '../../types';
import { formatMoneyWithSymbol, currencySymbols } from '../../lib/money-formatter';
import { IMoney, unitsValueMoney } from '../../lib/money';
import { formatTicketOrVoucherId } from '../../lib';
import { zeroMoney } from '../../lib/money';
import { useRewardTerminologyForPlayerCardOperator } from '../../betting/bet-with-credits';
import { Block, Heading, Text } from '../typography';
import { BetDetailsVariant } from '.';

export interface Props {
  bet: Pick<
    IBet,
    | 'betStatus'
    | 'boxed'
    | 'currency'
    | 'numLines'
    | 'payoutExpiresAt'
    | 'placedAt'
    | 'placedAtLocation'
    | 'playerCardId'
    | 'retailTicketId'
    | 'rewardCreditsPerLine'
    | 'stakePerLine'
    | 'typeName'
    | 'betLegs'
    | 'estimatedOdds'
    | 'price'
    | 'originalPrice'
  > & { legs: { length: Number } };
  getLocalDateTime(dateTime: Date): Date;
  variant?: BetDetailsVariant;
  venue: { label: string; isHighlighted: boolean };
  canViewTicketId?: boolean;
}

export const BetInfo = ({
  bet: {
    betLegs,
    betStatus,
    boxed,
    currency,
    legs,
    numLines,
    payoutExpiresAt,
    placedAt,
    playerCardId,
    retailTicketId,
    rewardCreditsPerLine,
    stakePerLine,
    typeName,
    price,
    placedAtLocation,
    originalPrice,
    estimatedOdds
  },
  getLocalDateTime,
  variant = 'default',
  venue,
  canViewTicketId
}: Props) => {
  const localDateTime = getLocalDateTime(new Date(placedAt));
  const placedDate = dayjs(localDateTime).format('MM/DD/YYYY hh:mm:ss A');
  const stakeMoney: IMoney = stakePerLine ? unitsValueMoney(stakePerLine) : unitsValueMoney(0);

  const isSuperParlay = betLegs != null && betLegs.length > 0;
  const { unit: rewardUnit } = useRewardTerminologyForPlayerCardOperator();

  return (
    <BetInfoWrapper variant={variant}>
      <BetInfoTopWrapper>
        <Column>
          <Row className="qa-bet-finder-bet-information">
            <span>Ticket Number:</span>
            <Data className="qa-bet-finder-id" weight={100}>
              {formatTicketOrVoucherId(retailTicketId, !canViewTicketId)}
            </Data>
          </Row>
          <Row>
            <span>Rewards Card:</span>
            <Data className="qa-bet-finder-player-card" weight={100}>
              {playerCardId || '-'}
            </Data>
          </Row>
        </Column>
        <Column>
          <Row highlight={venue.isHighlighted} data-testid="qa-venue-cell">
            <span>Issued At:</span>
            <Data className="qa-venue-cell" weight={100}>
              {venue.label}
            </Data>
          </Row>
          <Row>
            <span>Device:</span>
            <Data className="qa-device-cell'" weight={100}>
              {`${placedAtLocation?.deviceType} - ${placedAtLocation?.slotNumber}` || '-'}
            </Data>
          </Row>
        </Column>
        <Column>
          <Row justifyContentFlexEnd>
            <span>Bet Placed:</span>
            <Data className="qa-bet-finder-struck-time" weight={100}>
              {placedDate}
            </Data>
          </Row>
          {payoutExpiresAt !== null && (
            <Row highlight={betStatus === BetStatusType.Expired} justifyContentFlexEnd>
              <span>{betStatus === 'expired' ? 'Expired on' : 'Expires on'}:</span>
              <Data className="qa-bet-finder-collect-time">
                {dayjs(getLocalDateTime(new Date(payoutExpiresAt))).format('MM/DD/YYYY hh:mm:ss A')}
              </Data>
            </Row>
          )}
        </Column>
      </BetInfoTopWrapper>
      <Row noBottomMargin>
        <Column>
          <Text weight={100}>
            {isSuperParlay ? `${legs.length} Leg Super Parlay` : boxed ? `${typeName} Box` : typeName}
            {typeName && ' | '}x{numLines} bet @{' '}
            <strong>
              {rewardCreditsPerLine
                ? `${formatMoneyWithSymbol(
                    zeroMoney(),
                    '$'
                  )} | ${rewardCreditsPerLine.toLocaleString()} Reward ${rewardUnit}`
                : formatMoneyWithSymbol(stakeMoney, currencySymbols[currency])}
            </strong>
            {((price && originalPrice && !areEqualOdds(price, originalPrice)) || price || estimatedOdds) && ' | '}
            <BetOdds {...{ price, originalPrice, estimatedOdds }} />
          </Text>
        </Column>
      </Row>
    </BetInfoWrapper>
  );
};

const BetInfoWrapper = styled.div<{ variant?: BetDetailsVariant }>`
  display: flex;
  flex-direction: column;
  padding: ${({ variant }) => (variant === 'compact' ? '1rem' : '2.4rem')};
`;

const BetInfoTopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Row = styled.div<{ highlight?: boolean; noBottomMargin?: boolean; justifyContentFlexEnd?: boolean }>`
  display: flex;
  margin-right: 1.2rem;
  margin-bottom: 1rem;
  color: ${({ theme, highlight }) => (highlight ? theme.colors.status.error : theme.components.global.textColor)};

  ${({ noBottomMargin }) =>
    noBottomMargin &&
    `
    margin-bottom: 0;
  `}

  ${({ justifyContentFlexEnd }) =>
    justifyContentFlexEnd &&
    `
    justify-content: flex-end;
  `}
`;

const Data = styled(Text).attrs(() => ({ weight: 100 }))`
  padding-left: 0.5rem;
`;

const Column = styled(Heading).attrs(() => ({ as: 'div', size: 'lg' }))`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const BetOdds = ({
  price,
  estimatedOdds,
  originalPrice
}: {
  price: IPriceInput | null;
  originalPrice: IPriceInput | null;
  estimatedOdds: IPriceInput | null;
}) => {
  if (price && originalPrice && !areEqualOdds(price, originalPrice)) {
    return (
      <OddsBlock weight="bold" as="span">
        <OriginalOddsText>{originalPrice.a}</OriginalOddsText>
        <Text>{price.a}</Text>
      </OddsBlock>
    );
  }

  if (price) {
    return (
      <OddsBlock weight="bold" as="span">
        <Text>{price.a}</Text>
      </OddsBlock>
    );
  }

  if (estimatedOdds) {
    return (
      <OddsBlock weight="bold" as="span">
        {originalPrice && !areEqualOdds(originalPrice, estimatedOdds) && (
          <OriginalOddsText>{originalPrice.a}</OriginalOddsText>
        )}
        <Text>{estimatedOdds.a}</Text>
      </OddsBlock>
    );
  }

  return null;
};

const OddsBlock = styled(Block)`
  > * + * {
    margin-left: 0.5rem;
  }
`;

const OriginalOddsText = styled(Text)`
  text-decoration: line-through ${({ theme }) => theme.colors.red};
  color: ${({ theme }) => theme.colors.neutral};
`;

const areEqualOdds = (odds: IPriceInput | null, odds2: IPriceInput | null) =>
  odds && odds2 ? Boolean(odds.a === odds2.a) : false;
